import React, {lazy, Suspense, useCallback, useEffect, useState} from "react";
import {useIsLoggedIn} from "UserProvider";
import {isModalOpen, isPaletteHotkey, isTypingTarget} from "./paletteHotkey";

/**
 * The palette VIEW is a lazy chunk: importing it via `lazy(() => import(...))` keeps it (and the
 * registry, ranking and the upgrade-checkout dialog it pulls in) OUT of the eager entry bundle. It
 * is fetched on the FIRST open only.
 */
const CommandPalette = lazy(() => import("./CommandPalette"));

/**
 * Global Cmd/Ctrl+K host for the command palette (#168) — mounted with ONE line in `App.tsx`.
 *
 * It attaches a single window key listener that opens the palette on ⌘K / Ctrl+K, but ONLY when:
 *   - the user is logged in (public/SSG pages never arm it — the effect no-ops while logged out);
 *   - no modal is already open (a real dialog's Escape/focus-trap must win); and
 *   - focus is not in a text field (so the chord never hijacks typing).
 *
 * The view is dynamically imported on first open. Once opened it stays MOUNTED (hidden when closed)
 * so its own exit animation — and any dialog it spawned (the upgrade checkout) — can finish.
 */
export function useCommandPalette(): React.ReactElement | null {
	const loggedIn = useIsLoggedIn();
	const [mounted, setMounted] = useState(false);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!loggedIn) {
			return;
		}
		function onKeyDown(event: KeyboardEvent) {
			if (!isPaletteHotkey(event)) {
				return;
			}
			if (isModalOpen() || isTypingTarget(event.target)) {
				return;
			}
			event.preventDefault();
			setMounted(true);
			setOpen(true);
		}
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [loggedIn]);

	const close = useCallback(() => setOpen(false), []);

	if (!mounted) {
		return null;
	}
	return (
		<Suspense fallback={null}>
			<CommandPalette open={open} onClose={close}/>
		</Suspense>
	);
}
